import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { bloomsTaxonomy, getAllBloomLevels, getVerbsForLevel } from '../../data/bloomsTaxonomy';

const { FiInfo, FiChevronDown, FiChevronUp, FiBookOpen, FiTarget, FiCheckCircle, FiAlertTriangle } = FiIcons;

const BloomsTaxonomyHelper = ({ selectedLevel, onLevelChange, questionText, showAlignment = true }) => {
  const [showHelper, setShowHelper] = useState(false);
  const [expandedLevel, setExpandedLevel] = useState(null);
  
  const levels = getAllBloomLevels();
  const currentLevel = bloomsTaxonomy[selectedLevel];

  // Simple verb detection for alignment feedback
  const detectAlignment = () => {
    if (!questionText || !selectedLevel) return null;
    
    const text = questionText.toLowerCase();
    const levelVerbs = getVerbsForLevel(selectedLevel);
    const foundVerbs = levelVerbs.filter(verb => 
      text.includes(verb.toLowerCase())
    );
    
    return {
      hasAlignment: foundVerbs.length > 0,
      foundVerbs: foundVerbs.slice(0, 3), // Show max 3 verbs
      suggestedVerbs: levelVerbs.slice(0, 5) // Show 5 suggested verbs
    };
  };

  const alignment = showAlignment ? detectAlignment() : null;

  return (
    <div className="space-y-4">
      {/* Level Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Bloom's Taxonomy Level *
        </label>
        <div className="relative">
          <select
            value={selectedLevel}
            onChange={(e) => onLevelChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
            required
          >
            <option value="">Select Cognitive Level</option>
            {levels.map(level => (
              <option key={level.key} value={level.key}>
                Level {level.level}: {level.label}
              </option>
            ))}
          </select>
          <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Current Level Display */}
      {currentLevel && (
        <div className={`p-4 rounded-lg border ${currentLevel.color.replace('text-', 'border-').replace('-800', '-200')}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${currentLevel.color}`}>
                  Level {currentLevel.level}
                </span>
                <h4 className="font-medium text-gray-800">{currentLevel.label}</h4>
              </div>
              <p className="text-sm text-gray-600 mb-3">{currentLevel.description}</p>
              
              {/* Key Verbs Preview */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Key Action Verbs:</p>
                <div className="flex flex-wrap gap-1">
                  {currentLevel.verbs.slice(0, 8).map(verb => (
                    <span key={verb} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                      {verb}
                    </span>
                  ))}
                  {currentLevel.verbs.length > 8 && (
                    <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs">
                      +{currentLevel.verbs.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <button
              type="button"
              onClick={() => setShowHelper(!showHelper)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="View Taxonomy Guide"
            >
              <SafeIcon icon={FiInfo} />
            </button>
          </div>
        </div>
      )}

      {/* Alignment Feedback */}
      {alignment && questionText && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border ${
            alignment.hasAlignment 
              ? 'bg-green-50 border-green-200' 
              : 'bg-yellow-50 border-yellow-200'
          }`}
        >
          <div className="flex items-start space-x-2">
            <SafeIcon 
              icon={alignment.hasAlignment ? FiCheckCircle : FiAlertTriangle} 
              className={`mt-0.5 ${
                alignment.hasAlignment ? 'text-green-600' : 'text-yellow-600'
              }`} 
            />
            <div className="flex-1">
              <h4 className={`text-sm font-medium ${
                alignment.hasAlignment ? 'text-green-800' : 'text-yellow-800'
              }`}>
                {alignment.hasAlignment ? 'Good Alignment' : 'Consider Alignment'}
              </h4>
              
              {alignment.hasAlignment ? (
                <div className="mt-2">
                  <p className="text-xs text-green-700">
                    Found appropriate verbs: {alignment.foundVerbs.join(', ')}
                  </p>
                </div>
              ) : (
                <div className="mt-2">
                  <p className="text-xs text-yellow-700 mb-2">
                    No {currentLevel?.label} verbs detected in question stem.
                  </p>
                  <p className="text-xs text-yellow-600">
                    Consider using: {alignment.suggestedVerbs.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Taxonomy Helper Modal */}
      <AnimatePresence>
        {showHelper && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiBookOpen} className="text-primary-600" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      Bloom's Taxonomy Guide
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowHelper(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiChevronUp} />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Understanding cognitive levels for effective question design
                </p>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  {levels.map((level, index) => (
                    <div key={level.key} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedLevel(expandedLevel === level.key ? null : level.key)}
                        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${level.color}`}>
                              Level {level.level}
                            </span>
                            <span className="font-medium text-gray-800">{level.label}</span>
                          </div>
                          <SafeIcon 
                            icon={expandedLevel === level.key ? FiChevronUp : FiChevronDown} 
                            className="text-gray-400" 
                          />
                        </div>
                        <p className="text-sm text-gray-600 mt-1 text-left">{level.description}</p>
                      </button>

                      <AnimatePresence>
                        {expandedLevel === level.key && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-4 bg-white border-t border-gray-200">
                              {/* Action Verbs */}
                              <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-800 mb-2">
                                  Action Verbs ({level.verbs.length})
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                  {level.verbs.map(verb => (
                                    <span 
                                      key={verb} 
                                      className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs cursor-pointer transition-colors"
                                      title={`Click to use "${verb}" in your question`}
                                    >
                                      {verb}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Examples */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-800 mb-2">
                                  Example Questions
                                </h4>
                                <div className="space-y-1">
                                  {level.examples.map((example, idx) => (
                                    <div key={idx} className="text-sm text-gray-600 italic">
                                      "• {example}"
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BloomsTaxonomyHelper;